// src/offline/useOnlineStatus.ts
import { useEffect, useRef, useState } from "react";

export function useOnlineStatus() {
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const onlineRef = useRef(online);
  const offlineTimer = useRef<number | null>(null);

  const clearOfflineTimer = () => {
    if (offlineTimer.current) {
      window.clearTimeout(offlineTimer.current);
      offlineTimer.current = null;
    }
  };

  const setOnlineState = (value: boolean) => {
    onlineRef.current = value;
    setOnline(value);
  };

  useEffect(() => {
    onlineRef.current = online;
  }, [online]);

  useEffect(() => {

    const checkConnectivity = async () => {
      if (onlineRef.current) return;

      try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 4000);
        const res = await fetch("/manifest.json", {
          method: "HEAD",
          cache: "no-store",
          signal: controller.signal,
        });
        window.clearTimeout(timeoutId);
        setOnline(res.ok);
      } catch {
        setOnline(false);
      }
    };

    const onOnline = () => {
      clearOfflineTimer();
      setOnlineState(true);
    };

    // Debounce brief false offline events (seen on iOS Safari/PWA navigation)
    const onOffline = () => {
      onlineRef.current = false;
      clearOfflineTimer();
      offlineTimer.current = window.setTimeout(() => {
        setOnlineState(false);
      }, 700);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkConnectivity();
      }
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);

    // Recover from stale offline state while backgrounded
    const intervalId = window.setInterval(checkConnectivity, 45000);
    checkConnectivity();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
      clearOfflineTimer();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, []);

  return online;
}
